import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import femaleWorker from "../assets/FemaleWorker.png"

export default function About({ aboutData = {} }) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;

  const highlightText = (text = "", highlights = []) => {
    if (!text || highlights.length === 0) return text;
    const regex = new RegExp(`\\b(${highlights.join("|")})\\b`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (highlights.some(word => word.toLowerCase() === part.toLowerCase())) {
        return (
          <span
            key={index}
            className="text-secondary font-semibold"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <section id="about" className="relative py-12 lg:py-16 bg-primary-dark md:bg-transparent">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
          {/* Left Side - Content */}
          <motion.div
            className="w-full lg:w-1/2 flex flex-col justify-center"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6 }}
            viewport={{ once: true }}
            data-testid="about-content"
          >
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              <span className="text-secondary">About</span> Us
            </h2>

            <div className="mb-8">
              <p className="text-lg lg:text-xl text-slate-200 leading-relaxed">
                {highlightText(aboutData.aboutParagraph, aboutData.highlightedWords)}
              </p>
            </div>

            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                className="group p-6 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: disableMotion ? 0 : 0.4, delay: disableMotion ? 0 : 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-heading font-semibold mb-3 flex items-center text-white">
                  <span className="w-10 h-1 bg-secondary rounded-full mr-3"></span>
                  Our Mission
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {aboutData.mission}
                </p>
              </motion.div>

              <motion.div
                className="group p-6 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: disableMotion ? 0 : 0.4, delay: disableMotion ? 0 : 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-heading font-semibold mb-3 flex items-center text-white">
                  <span className="w-10 h-1 bg-secondary rounded-full mr-3"></span>
                  Our Vision
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {aboutData.vision}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Image & Commitments */}
          <motion.div
            className="relative w-full lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: disableMotion ? 0 : 0.6, delay: disableMotion ? 0 : 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative w-full flex flex-col lg:flex-row items-start gap-6 lg:gap-8">
              {/* Female Worker Image */}
              <div className="relative w-full lg:w-auto overflow-hidden rounded-2xl lg:mr-[200px]" >
                <img
                  src={femaleWorker}
                  alt="Professional construction engineer"
                  className="w-full lg:w-[450px] h-auto object-cover min-h-[300px] lg:min-h-[600px] lg:mr-4"
                  style={{
                    objectPosition: 'top center',
                    maskImage: 'linear-gradient(to bottom, black 90%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 90%, transparent 100%)'
                  }}
                />
                {/* <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/40 to-transparent"></div> */}
              </div>

              {/* Commitments Card */}
              <motion.div
                className="w-full lg:w-[280px] lg:absolute lg:-right-4 lg:top-[5%] z-20"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: disableMotion ? 0 : 0.4, delay: disableMotion ? 0 : 0.3 }}
                viewport={{ once: true }}
              >
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-white/30 shadow-2xl">
                  <h3 className="text-xl font-heading font-bold text-white mb-4">
                    Our Commitments
                  </h3>

                  <div className="space-y-4">
                    {/* Commitment 1 */}
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                      <motion.div 
                        className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary/20 ring-1 ring-secondary/40 shadow-md shadow-secondary/30 flex items-center justify-center flex-shrink-0 overflow-hidden"
                        whileHover={disableMotion ? {} : { scale: 1.03 }}
                        transition={disableMotion ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 10 }}
                      >
                        <img src={aboutData.commitment1Icon} alt="" className="w-6 h-6 md:w-6 md:h-6 object-contain" />
                      </motion.div>
                      <div>
                        <h4 className="font-bold text-secondary text-base">{aboutData.commitment1Title}</h4>
                        <p className="text-sm font-semibold text-slate-300 mt-1">{aboutData.commitment1Description}</p>
                      </div>
                    </div>

                    {/* Commitment 2 */}
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                      <motion.div 
                        className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary/20 ring-1 ring-secondary/40 shadow-md shadow-secondary/30 flex items-center justify-center flex-shrink-0 overflow-hidden"
                        whileHover={disableMotion ? {} : { scale: 1.03 }}
                        transition={disableMotion ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 10 }}
                      >
                        <img src={aboutData.commitment2Icon} alt="" className="w-6 h-6 md:w-6 md:h-6 object-contain" />
                      </motion.div>
                      <div>
                        <h4 className="font-bold text-secondary text-base">{aboutData.commitment2Title}</h4>
                        <p className="text-sm font-semibold text-slate-300 mt-1">{aboutData.commitment2Description}</p>
                      </div>
                    </div>

                    {/* Commitment 3 */}
                    <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                      <motion.div 
                        className="w-11 h-11 md:w-9 md:h-9 rounded-lg bg-secondary/20 ring-1 ring-secondary/40 shadow-md shadow-secondary/30 flex items-center justify-center flex-shrink-0 overflow-hidden"
                        whileHover={disableMotion ? {} : { scale: 1.03 }}
                        transition={disableMotion ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 10 }}
                      >
                        <img src={aboutData.commitment3Icon} alt="" className="w-6 h-6 md:w-6 md:h-6 object-contain" />
                      </motion.div>
                      <div>
                        <h4 className="font-bold text-secondary text-base">{aboutData.commitment3Title}</h4>
                        <p className="text-sm font-semibold text-slate-300 mt-1">{aboutData.commitment3Description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
